'use client'

/**
 * "Which Michael Chen do you mean?" — one entity step, as one-click choices
 * (#16822, PRD R1.8).
 *
 * Each choice names the person and the detail that tells them apart from a
 * same-named colleague ("7th grade · Riverbend Middle"). Choosing records
 * the pick on the turn's `ai_chat_resolved_entities` row
 * (`pickResolvedEntityAction`), so later turns in the conversation do not
 * ask again (R1.10), and then asks the question again through the composer
 * path (`onAsk`) CARRYING the person chosen. The server resolves the re-ask
 * to that person when they are one of the name's in-scope matches, so the
 * answer is about them whether or not the pick was recorded.
 *
 * A pick that fails to record — the action throws or answers
 * `success: false` — is surfaced with a toast, and the ask still goes, about
 * the person chosen; only a LATER question about the same name may ask
 * again. A `nearest` step carries no row: its re-ask names the person.
 *
 * One pick per step: once a choice is taken every choice on the step is
 * disabled, so a double click cannot launch two follow-up turns.
 *
 * Native buttons in a labelled group: reachable and operable by keyboard
 * with no focus management of its own. Plain text only, semantic tokens.
 */

import { useId, useState } from 'react'

import { pickResolvedEntityAction } from '@/app/actions/resolved-entities'
import { User } from '@/components/icons'
import type { EntityMatch, EntityStep } from '@/lib/ai-chat/next-steps'
import type { EntityPin } from '@/lib/ai-chat/resolve-entities'
import { toastError } from '@/lib/toast'
import { cn } from '@/lib/utils'

/** Whether the pick landed on the turn's row. Never throws. */
async function recordPick(
  resolvedEntityId: string,
  personId: string,
): Promise<boolean> {
  try {
    const result = await pickResolvedEntityAction({
      resolvedEntityId,
      personId,
    })
    return result?.success === true
  } catch {
    // Logged server-side by `orgAction`; the caller surfaces it — see sub-issue R carve-out
    return false
  }
}

export function EntityChoices({
  step,
  onAsk,
  disabled = false,
  className,
}: {
  step: EntityStep
  /** Ask a question as the asker's next turn, about the person chosen. */
  onAsk: (question: string, entityPick?: EntityPin) => void
  /** True while a turn is streaming: a pick would race it. */
  disabled?: boolean
  className?: string
}) {
  const headingId = useId()
  const [taken, setTaken] = useState<string | null>(null)

  async function choose(match: EntityMatch) {
    setTaken(match.personId)
    if (
      step.resolvedEntityId &&
      !(await recordPick(step.resolvedEntityId, match.personId))
    ) {
      toastError({
        message:
          'Could not save that choice, so a later question may ask again.',
        context: { action: 'pickResolvedEntity' },
      })
    }
    onAsk(match.question, { personId: match.personId })
  }

  return (
    <div
      role="group"
      aria-labelledby={headingId}
      data-testid="ai-chat-entity-choices"
      className={cn('mt-2', className)}
    >
      <p id={headingId} className="text-sm font-medium text-text-primary">
        {step.question}
      </p>
      <ul className="mt-1.5 flex flex-col gap-1.5">
        {step.matches.map(match => (
          <li key={match.personId}>
            <button
              type="button"
              data-testid="ai-chat-entity-choice"
              disabled={disabled || taken !== null}
              aria-pressed={taken === match.personId}
              onClick={() => void choose(match)}
              className={cn(
                'flex w-full items-start gap-2 rounded-md border border-solid border-stroke-light px-3 py-2 text-left',
                'hover:bg-fill-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-selected',
                'disabled:cursor-not-allowed disabled:opacity-60 pointer-coarse:min-h-11',
              )}
            >
              <User
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-secondary"
                aria-hidden
              />
              <span className="min-w-0">
                <span className="block text-sm text-text-primary">
                  {match.label}
                </span>
                {match.detail && (
                  <span className="block text-xs text-text-secondary">
                    {match.detail}
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
